import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import CreateDisputePage from '@/pages/CreateDisputePage';
import DisputeCenter from '@/pages/DisputeCenter';
import EducationCenter from '@/pages/EducationCenter';
import AccountSettings from '@/pages/AccountSettings';
import TermsOfUse from '@/pages/TermsOfUse';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import BrokerDashboard from '@/pages/broker/BrokerDashboard';
import BrokerClients from '@/pages/broker/BrokerClients';
import BrokerClientDetail from '@/pages/broker/BrokerClientDetail';
import BrokerCreateDispute from '@/pages/broker/BrokerCreateDispute';
import CreateDispute from '@/pages/broker/CreateDispute';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const adminFlag = localStorage.getItem('isAdmin') === 'true';
    setIsAdmin(adminFlag);
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (!adminFlag) {
        fetchUser();
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setIsAdmin(false);
    localStorage.setItem('isAdmin', 'false');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const adminLogin = (newToken) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('isAdmin', 'true');
    setToken(newToken);
    setIsAdmin(true);
    setUser(null);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    setToken(null);
    setUser(null);
    setIsAdmin(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const isBroker = user?.account_type === 'broker';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isAdmin, isBroker, login, adminLogin, logout, token }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={
            user ? (isBroker ? <Navigate to="/broker" /> : <Navigate to="/dashboard" />) : 
            (isAdmin ? <Navigate to="/admin" /> : <LandingPage />)
          } />
          <Route path="/terms" element={<TermsOfUse />} />
          
          {/* DIY Client Routes */}
          <Route path="/dashboard" element={user && !isBroker ? <Dashboard /> : <Navigate to="/" />} />
          <Route path="/disputes" element={user && !isBroker ? <DisputeCenter /> : <Navigate to="/" />} />
          <Route path="/disputes/create" element={user && !isBroker ? <CreateDisputePage /> : <Navigate to="/" />} />
          <Route path="/education" element={user ? <EducationCenter /> : <Navigate to="/" />} />
          <Route path="/settings" element={user ? <AccountSettings /> : <Navigate to="/" />} />
          
          {/* Broker Routes */}
          <Route path="/broker" element={user && isBroker ? <BrokerDashboard /> : <Navigate to="/" />} />
          <Route path="/broker/clients" element={user && isBroker ? <BrokerClients /> : <Navigate to="/" />} />
          <Route path="/broker/clients/:clientId" element={user && isBroker ? <BrokerClientDetail /> : <Navigate to="/" />} />
          <Route path="/broker/clients/:clientId/disputes/create" element={user && isBroker ? <BrokerCreateDispute /> : <Navigate to="/" />} />
          <Route path="/broker/disputes/create/:clientId" element={user && isBroker ? <CreateDispute /> : <Navigate to="/" />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={!isAdmin ? <AdminLogin /> : <Navigate to="/admin" />} />
          <Route path="/admin" element={isAdmin ? <AdminDashboard /> : <Navigate to="/admin/login" />} />
          
          {/* Payment Routes */}
          <Route path="/payment/success" element={user ? <PaymentSuccess /> : <Navigate to="/" />} />
          <Route path="/payment/cancel" element={user ? <PaymentCancel /> : <Navigate to="/" />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
export { API };